import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BOTTOM_INDENTS, clampToPerformanceRetentionFloor, ROUTE_PATHS } from '@constants';
import { Divider, RedirectLink, Typography } from '@ui';
import { dayjs } from '@utils';
import { Col, Row } from 'antd';
import { observer } from 'mobx-react';
import Breakdowns from './components/Breakdowns';
import Menu from './components/Menu';
import Overview from './components/Overview';
import Reports from './components/Reports';
import { ALL_FILTER_VALUE, FILTER_PARAM_KEYS, FILTER_PARAMS, MENU_ITEM_IDS } from './constants';
import { FILTER_DEFAULTS, getFilters, readFiltersFromStorage, saveFiltersToStorage } from './utils';

const Performance = observer(() => {
    const [activeAnchor, setActiveAnchor] = useState(MENU_ITEM_IDS.OVERVIEW);
    const [oldAnchor, setOldAnchor] = useState(MENU_ITEM_IDS.OVERVIEW);
    const [searchParams, setSearchParams] = useSearchParams();
    const [savedFilters, setSavedFilters] = useState(() => {
        const hasParams = FILTER_PARAM_KEYS.some((key) => searchParams.has(key));
        return hasParams ? null : readFiltersFromStorage();
    });

    const {
        [FILTER_PARAMS.DATE_FROM]: restoredDateFrom,
        [FILTER_PARAMS.DATE_TO]: restoredDateTo,
        [FILTER_PARAMS.CAMPAIGN_ID]: campaign,
        [FILTER_PARAMS.DEVICE_TYPE]: device,
        [FILTER_PARAMS.CUSTOMER_TYPE]: customer,
        [FILTER_PARAMS.DETAILIZATION_TAB]: detailizationTab
    } = getFilters(searchParams, savedFilters, FILTER_DEFAULTS);

    // A range persisted before the 2-year retention floor existed would 400 on the first render
    const dateFrom = clampToPerformanceRetentionFloor(restoredDateFrom);
    const dateTo = clampToPerformanceRetentionFloor(restoredDateTo, { endOfDay: true });

    const date = useMemo(
        () => ({ [FILTER_PARAMS.DATE_FROM]: dateFrom, [FILTER_PARAMS.DATE_TO]: dateTo }),
        [dateFrom, dateTo]
    );

    useEffect(() => {
        if (savedFilters && Object.keys(savedFilters).length > 0) {
            // Seed from the clamped dates so the URL and localStorage stop advertising an out-of-retention range.
            const seeded = {
                ...savedFilters,
                [FILTER_PARAMS.DATE_FROM]: dateFrom,
                [FILTER_PARAMS.DATE_TO]: dateTo
            };
            saveFiltersToStorage(seeded);
            setSearchParams(seeded, { replace: true });
        } else if (dateFrom !== restoredDateFrom || dateTo !== restoredDateTo) {
            const next = new URLSearchParams(searchParams);
            next.set(FILTER_PARAMS.DATE_FROM, dateFrom);
            next.set(FILTER_PARAMS.DATE_TO, dateTo);
            saveFiltersToStorage(Object.fromEntries(next));
            setSearchParams(next, { replace: true });
        }
        setSavedFilters(null);
    }, []);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (!value || value === ALL_FILTER_VALUE) next.delete(key);
        else next.set(key, value);
        saveFiltersToStorage(Object.fromEntries(next));
        setSearchParams(next);
    };

    const handleDateChange = ({ [FILTER_PARAMS.DATE_FROM]: dateFrom, [FILTER_PARAMS.DATE_TO]: dateTo }) => {
        const next = new URLSearchParams(searchParams);
        next.set(FILTER_PARAMS.DATE_FROM, dateFrom);
        next.set(FILTER_PARAMS.DATE_TO, dateTo);
        saveFiltersToStorage(Object.fromEntries(next));
        setSearchParams(next);
    };

    // TODO: rewrite EmptyState component and it's logic after PO's review
    // if (!overviewPending && !chartDatasets.length) return <EmptyState />;

    const onComponentInViewUpdate = ({ newAnchor, isInView }) => {
        if (isInView) {
            setOldAnchor(activeAnchor);
            setActiveAnchor(newAnchor);
        }

        if (!isInView && newAnchor === activeAnchor && oldAnchor) {
            setOldAnchor(activeAnchor);
            setActiveAnchor(oldAnchor);
        }
    };

    return (
        <Fragment>
            <Typography.Text data-e2e='performance_page-title' style={BOTTOM_INDENTS.XL_PLUS} type='em0'>
                Performance
            </Typography.Text>
            <Row style={BOTTOM_INDENTS.XL_PLUS} gutter={[16, 16]} justify='middle'>
                <Col data-e2e='performance_page-description' flex='1 0 auto'>
                    Access performance metrics for your marketing campaigns. For 3rd-party reporting settings (e.g. GA),
                    click{' '}
                    <RedirectLink
                        data-e2e='performance_settings-redirect_link'
                        display='inline'
                        text='here'
                        fontSize='14px'
                        fontWeight='400'
                        to={ROUTE_PATHS.TRACKING_SETTINGS}
                    />
                    .
                </Col>
            </Row>

            <Menu
                onDateChange={handleDateChange}
                dateValue={[dayjs(dateFrom), dayjs(dateTo)]}
                activeAnchor={activeAnchor}
            />

            <Overview
                date={date}
                filters={{ campaign, device, customer, detailizationTab }}
                onFiltersChange={{
                    campaign: (val) => updateParam(FILTER_PARAMS.CAMPAIGN_ID, val),
                    device: (val) => updateParam(FILTER_PARAMS.DEVICE_TYPE, val),
                    customer: (val) => updateParam(FILTER_PARAMS.CUSTOMER_TYPE, val),
                    detailizationTab: (val) => updateParam(FILTER_PARAMS.DETAILIZATION_TAB, val)
                }}
                onComponentViewUpdate={(isInView) =>
                    onComponentInViewUpdate({ newAnchor: MENU_ITEM_IDS.OVERVIEW, isInView })
                }
            />
            <Divider margin='64px 0' />
            <Breakdowns
                date={date}
                onComponentViewUpdate={(isInView) =>
                    onComponentInViewUpdate({ newAnchor: MENU_ITEM_IDS.BREAKDOWNS, isInView })
                }
            />
            <Divider margin='64px 0' />
            <Reports
                date={date}
                onComponentViewUpdate={(isInView) =>
                    onComponentInViewUpdate({ newAnchor: MENU_ITEM_IDS.REPORTS, isInView })
                }
            />
        </Fragment>
    );
});

export default Performance;
